// src/hooks/useCrews.js
import { useState, useEffect, useCallback } from 'react';
import {
  loadCrews,
  createCrew,
  joinCrew,
  leaveCrew,
  deleteCrew,
  loadCrewMembers
} from '../api/crewsApi';

export function useCrews() {
  const [crews, setCrews] = useState([]);
  const [crewMembers, setCrewMembers] = useState({});
  const [selectedCrewId, setSelectedCrewId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await loadCrews();
      setCrews(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch {
      setCrews([]);
      return [];
    }
  }, []);

  const create = useCallback(async title => {
    const result = await createCrew(title);
    await load();
    return result;
  }, [load]);

  const join = useCallback(async id => {
    const result = await joinCrew(id);
    await load();
    return result;
  }, [load]);

  const leave = useCallback(async id => {
    const result = await leaveCrew(id);
    await load();
    return result;
  }, [load]);

  const remove = useCallback(async id => {
    await deleteCrew(id);
    await load();
  }, [load]);

  const loadMembers = useCallback(async crewId => {
    if (!crewId) return [];
    setSelectedCrewId(crewId);

    try {
      const data = await loadCrewMembers(crewId);
      const ids = Array.isArray(data)
        ? data.map(user => Number(user.ID ?? user.id))
        : [];
      setCrewMembers(prev => ({ ...prev, [crewId]: ids }));
      return ids;
    } catch (error) {
      setCrewMembers(prev => ({ ...prev, [crewId]: [] }));
      return [];
    }
  }, []);

  const loadAllMembers = useCallback(async crewList => {
    const accessibleCrews = (Array.isArray(crewList) ? crewList : []).filter(
      crew => crew && (crew.can_view_private_data || crew.is_member || crew.is_owner)
    );

    await Promise.all(accessibleCrews.map(crew => loadMembers(crew.id)));
  }, [loadMembers]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (crews.length) {
      loadAllMembers(crews);
    }
  }, [crews, loadAllMembers]);

  return {
    crews,
    crewMembers,
    selectedCrewId,
    loadMembers,
    create,
    join,
    leave,
    remove,
    reload: load
  };
}

export default useCrews;
